import { ChangeDetectionStrategy, Component, OnInit, DoCheck } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ErisFormFieldType } from 'eris-forms';

@Component({
  selector: 'lib-step',
  template: `
    <lib-tab-wrapper (selectedTab)="selectedTab($event)" [simpleStep]="field.templateOptions?.simpleStep">
      <ng-container *ngFor="let tab of filterField(field.fieldGroup); let i = index; let last = last;">
        <lib-tab
          [active]="active === i"
          [disabled]="i !== 0 && i > active && !isValid(field.fieldGroup[i - 1], active, i, 'tab-click')"
          [tabTitle]="tab.templateOptions?.title"
          [tabDescription]="tab.templateOptions?.description"
          [tabIcon]="tab.templateOptions?.icon">
          <formly-field [field]="tab"></formly-field>
          <div class="flex justify-between my-5">
            <p type="button"
               (click)="previous()"
               *ngIf="i !== 0; else placeholder"
               class="text-white px-4 py-2 rounded-md bg-green-500 cursor-pointer">
              <span class="fa fa-arrow-left pr-3"></span> Previous
            </p>
            <ng-template #placeholder><span></span></ng-template>
            <div class="flex items-center">
              <button
                type="button"
                (click)="next(!isValid(field.fieldGroup[i], active, i, 'next'))"
                *ngIf="!last"
                class="text-white px-4 py-2 rounded-md bg-green-500 cursor-pointer"
                [class.opacity-40]="!isValid(field.fieldGroup[i], active, i, 'next')">
                Next <span class="fa fa-arrow-right pl-3"></span>
              </button>
              <button
                *ngIf="last"
                class="text-white px-4 py-2 rounded-md bg-green-500"
                [disabled]="!form.valid"
                type="submit">
                Submit
              </button>
              <button
                *ngIf="canSaveDraft()"
                type="button"
                (click)="onSaveDraft()"
                class="text-white px-4 py-2 rounded-md bg-green-500 cursor-pointer"
                style="margin-left: 10px;">
                Save
              </button>
            </div>
          </div>
        </lib-tab>
      </ng-container>
    </lib-tab-wrapper>
  `,
  changeDetection: ChangeDetectionStrategy.Default
})
export class CustomStepComponent extends ErisFormFieldType implements OnInit, DoCheck {
  active = 0;
  private userNavigated = false;
  private isSaving = false;

  filterField = (fieldGroup: FormlyFieldConfig[]) => (fieldGroup || []).filter(fg => !fg.hide);

  ngOnInit(): void {
    const step = this.model?.activeStep ?? this.options?.formState?.activeStep;
    if (typeof step === 'number') {
      this.active = step;
    }
  }

  ngDoCheck(): void {
    if (!this.userNavigated) {
      const step = this.model?.activeStep ?? this.options?.formState?.activeStep;
      if (typeof step === 'number' && this.active !== step) {
        this.active = step;
      }
    }
  }

  selectedTab({ index }: { index: number }): void {
    this.userNavigated = true;
    this.active = index;
    if (this.model) this.model.activeStep = index;
    if (this.options?.formState) this.options.formState.activeStep = index;
  }

  previous(): void {
    this.userNavigated = true;
    this.active = this.active - 1;
    if (this.model) this.model.activeStep = this.active;
    if (this.options?.formState) this.options.formState.activeStep = this.active;
  }

  next(disabled?: boolean): void {
    if (!disabled) {
      this.userNavigated = true;
      this.active = this.active + 1;
      if (this.model) this.model.activeStep = this.active;
      if (this.options?.formState) this.options.formState.activeStep = this.active;
    }
  }

  isValid(field: FormlyFieldConfig, active: number, index: number, btnFunction = ''): boolean {
    if ((active + 1) < index) {
      return false;
    }
    if (field?.templateOptions?.extraData?.isCustomField) {
      if (btnFunction === 'next') {
        const correctField = this.filterField(this.field.fieldGroup)[index];
        return correctField?.templateOptions?.extraData?.isValid;
      }
      return field?.templateOptions?.extraData?.isValid;
    }
    if (field?.key) {
      return field.formControl ? field.formControl.valid : true;
    }
    if (field?.fieldGroup) {
      return field.fieldGroup.every(f => this.isValid(f, active, index));
    }
    return true;
  }

  canSaveDraft(tab?: any): boolean {
    if (this.field?.templateOptions && typeof this.field.templateOptions['saveDraft'] === 'function') {
      return true;
    }
    if (this.field?.templateOptions && this.field.templateOptions['showSaveDraft']) {
      return true;
    }
    if (this.options?.formState && typeof this.options.formState.saveDraft === 'function') {
      return true;
    }
    return false;
  }

  onSaveDraft(): void {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;
    setTimeout(() => {
      this.isSaving = false;
    }, 1500);

    const currentModel = this.model || (this.form ? this.form.value : {});
    if (currentModel) {
      currentModel.activeStep = this.active;
    }
    if (this.options?.formState) {
      this.options.formState.activeStep = this.active;
    }

    if (this.field?.templateOptions && typeof this.field.templateOptions['saveDraft'] === 'function') {
      this.field.templateOptions['saveDraft'](currentModel, this.form);
    } else if (this.options?.formState && typeof this.options.formState.saveDraft === 'function') {
      this.options.formState.saveDraft(currentModel, this.form);
    } else {
      const event = new CustomEvent('eris-save-draft', { detail: { model: currentModel, form: this.form, activeStep: this.active }, bubbles: true });
      window.dispatchEvent(event);
    }
  }
}
